import { UAParser } from 'ua-parser-js';
import store, { emptyCart } from '@/store';
import { withAuth } from '@/auth';
import odooClient, { withLocale } from '@/odooClient';
import cloneDeep from 'lodash.clonedeep';
import deepMerge from 'lodash.merge';

export default (() => ({
  /**
   * Add or update line in cart.
   *
   * @param {Number} lineId
   * @param {String} type
   * @param {Object} route
   * @param {Object} order
   * @param {Array} attachments
   * @param {Object} fields
   *
   * @return {Object}
   */
  async upsert(
    lineId,
    type,
    { name, params, query },
    order,
    attachments = [],
    {
      product = {},
      image,
      fileWithTags,
      file,
      tagScale,
    } = {},
  ) {
    const { id, display_name: displayName, full_name_translated: fullName } = product;
    const uuid = crypto.randomUUID();
    const data = {
      id: lineId,
      type,
      order,
      attachments,
      fields: {
        product: { id, display_name: displayName, full_name_translated: fullName },
        image,
        fileWithTags,
        file,
        tagScale,
      },
      route: { name, params, query },
      verified: true,
    };

    const existing = store.cart.lines.find((l) => l.id === lineId);
    if (existing) {
      Object.assign(existing, data, {
        uuid,
      });
    } else {
      store.cart.lines.push({
        ...data,
        id: Math.floor(Math.random() * Date.now()),
        uuid,
      });
    }
  },
  /**
   * Get line by id.
   *
   * @param {Number} id
   *
   * @return {Object}
   */
  getLine(id) {
    return store.cart.lines.find((l) => l.id === id);
  },
  /**
   * Fetch order line.
   *
   * @param {Object} line
   * @param {Object} context
   *
   * @return {Promise}
   */
  async fetchOrderLine(line, context) {
    let condition;

    if (line.has_uuid) {
      condition = ['search_read', [[
        ['uuid', '=', line.uuid],
      ]]];
    } else {
      condition = ['read', [line.lineId]];
    }

    const args = await withAuth([
      'sale.order.line',
      ...condition,
      withLocale(context),
    ]);

    return odooClient.request('call', {
      service: 'object',
      method: 'execute_kw',
      args,
    });
  },
  /**
   * Whether cart contains max OSA lines.
   *
   * @return {Boolean}
   */
  hasMaxOsa() {
    return store.cart.lines.filter((l) => this.isOsa(l.type)).length > 1;
  },
  /**
   * Wether given type is OSA.
   *
   * @param {String} type
   *
   * @return {Boolean}
   */
  isOsa(type) {
    const types = ['custom', 'brigante'];
    return types.includes(type);
  },
  /**
   * Empty's the cart.
   */
  empty() {
    store.cart = cloneDeep(emptyCart);
  },
  /**
   * Restore cart from existing order.
   *
   * @param {Object} cart
   * @param {Number} originalOrderId
   * @param {Number} orderEditId
   */
  restore(cart, originalOrderId, orderEditId = null) {
    store.cart = deepMerge({}, emptyCart, cart);
    store.cart.lines = this.normalizeLines(store.cart.lines);
    store.cart.order_id = orderEditId;

    if (!orderEditId) {
      store.cart.original_order_id = originalOrderId;
    }
  },
  /**
   * Normalize restored cart lines.
   *
   * @param {Array} lines
   *
   * @return {Array}
   */
  normalizeLines(lines = []) {
    return lines.map((line) => {
      const normalizedId = [null, undefined, ''].includes(line.id)
        ? Math.floor(Math.random() * Date.now())
        : line.id;

      return {
        ...line,
        id: normalizedId,
        route: {
          ...line.route,
          query: {
            ...line.route?.query,
            line: `${normalizedId}`,
          },
        },
      };
    });
  },
  /**
   * Restore cart from existing legacy order (< v4).
   * These orders don't have a cart
   *
   * @param {Object} order
   * @param {Number} originalOrderId
   * @param {Number} orderEditId
   */
  restoreLegacy(order, originalOrderId, orderEditId = null) {
    let route = {};
    const lineId = Math.floor(Math.random() * Date.now());
    this.empty();

    if (order.product_id) {
      route = {
        name: 'order',
        params: {
          variant: order.product_id,
        },
      };
    } else {
      route = {
        name: 'custom-order',
      };
    }

    this.upsert(
      lineId,
      order.product_id ? 'brigante' : 'custom',
      route,
      {
        id: originalOrderId,
        has_shaft: true,
        has_bottoming: true,
        leg_measurements: {
          left: {},
          right: {},
        },
        upper_height: [
          {
            type: {},
            last_type: {},
            method: {},
            height: {},
          },
          {
            type: {},
            last_type: {},
            method: {},
            height: {},
          },
        ],
        tags: [],
        tags_scale: 1,
        existing_model: null,
        sockshoe_enabled: false,
        boarding_category: {},
        heel_stiffener: [
          {
            type: { id: null }, variant: { id: null }, height: { id: null }, last_annotation: null,
          },
          {
            type: { id: null }, variant: { id: null }, height: { id: null }, last_annotation: null,
          },
        ],
        nose_material_right: {},
        nose_material_left: {},
        heel_type: {},
        heel_height: [],
        rolling: {
          left: {},
          right: {},
        },
        sole_material: {},
        ...order,
      },
      [],
      {
        product: {
          display_name: order.product,
        },
      },
    );

    store.cart.order_id = orderEditId;

    if (!orderEditId) {
      store.cart.original_order_id = originalOrderId;
    }

    store.cart.initial_app_version = order.initial_app_version || '3.0.0';
    store.cart.app_version = order.app_version || '3.0.0';
    store.cart.client.salutation = order.salutation;
    store.cart.client.customer_name = order.customer_name;
    store.cart.client.mold_number = order.last_no;
  },
  /**
   * Serialize cart (and remove any attachments).
   *
   * @return {String}
   */
  serialize() {
    const lines = store.cart.lines.map((l) => ({
      ...l,
      attachments: undefined,
      verified: undefined,
      has_uuid: true,
      fields: {
        product: l.fields.product,
      },
    }));
    const parser = new UAParser();

    return JSON.stringify({
      cart: {
        ...store.cart,
        lines,
        app_version: import.meta.env.PACKAGE_VERSION,
        initial_app_version: store.cart.initial_app_version || import.meta.env.PACKAGE_VERSION,
        initial_device: store.cart.initial_device || parser.getResult(),
      },
    });
  },
}))();
